import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero, Eyebrow } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem, Counter } from "@/components/site/motion";
import { COUNTRIES, EXPANSION_MARKETS, IMAGES } from "@/data/content";

export default function Marches() {
  return (
    <>
      <PageHero
        eyebrow="Marchés stratégiques"
        titleLines={["De l'Europe", "à l'Afrique de l'Ouest"]}
        subtitle="Un positionnement unique : la France & Outre-mer comme base européenne, le Bénin et la Côte d'Ivoire comme marchés d'ancrage africains."
        image={IMAGES.abidjanLagoon}
      />

      <section className="bg-cream py-24 sm:py-32" data-testid="markets-countries">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Positionnement</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium text-navy">
              Un positionnement <em className="not-italic italic text-gold-gradient">unique</em>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {COUNTRIES.map((c) => (
              <Reveal key={c.key} delay={0.1}>
                <div className="card-lumen overflow-hidden rounded-3xl border border-navy/10 bg-white hover:border-gold h-full" data-testid={`country-card-${c.key}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img src={c.image} alt={`${c.name}, ${c.city}`} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
                    <div className="absolute bottom-4 left-5">
                      <span className="text-xs uppercase tracking-[0.2em] text-gold-light">{c.tag}</span>
                      <p className="font-display text-3xl text-white">{c.flag} {c.name}</p>
                    </div>
                  </div>
                  <div className="p-8">
                    <p className="text-anthracite font-body leading-relaxed">{c.desc}</p>
                    <div className="mt-6 grid grid-cols-2 gap-4">
                      {c.stats.map((s) => (
                        <div key={s.v} className="rounded-xl bg-cream p-4">
                          <div className="font-display text-2xl font-semibold text-gold-gradient">{s.k}</div>
                          <p className="text-xs text-anthracite font-body">{s.v}</p>
                        </div>
                      ))}
                    </div>
                    <ul className="mt-6 space-y-2">
                      {c.atouts.map((a) => (
                        <li key={a} className="flex items-center gap-2 text-sm text-anthracite font-body">
                          <Check size={16} className="text-gold shrink-0" /> {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-night py-20" data-testid="markets-expansion">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 text-center">
          <Eyebrow light>Expansion</Eyebrow>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium text-white">Expansion vers d'autres marchés africains</h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {EXPANSION_MARKETS.map((m) => (
              <span key={m} className="rounded-full border border-gold/40 px-6 py-3 font-display text-white hover:bg-gold/10 transition-colors">{m}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-24 sm:py-32" data-testid="markets-future">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-3xl">
              <img src={IMAGES.heroAbidjan} alt="Skyline moderne d'Abidjan" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Vision</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium text-navy leading-tight">
              L'Afrique, l'avenir des <em className="not-italic italic text-gold-gradient">investissements</em>
            </h2>
            <p className="mt-6 text-anthracite font-body leading-relaxed">
              Avec une population de <strong className="text-navy">1,4 milliard</strong> d'habitants, la croissance la plus rapide au monde et la plus grande zone de libre-échange (ZLECAf), le continent redéfinit la carte des opportunités mondiales.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <div><div className="font-display text-4xl text-gold-gradient font-semibold"><Counter value={1.4} decimals={1} suffix=" Md" /></div><p className="text-sm text-anthracite">Population</p></div>
              <div><div className="font-display text-4xl text-gold-gradient font-semibold"><Counter value={1.3} decimals={1} suffix=" Md+" /></div><p className="text-sm text-anthracite">Marché ZLECAf</p></div>
            </div>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-gold font-semibold hover:gap-3 transition-all">Investir avec nous <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
